import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { cn } from '@/lib/utils';

interface FormOtpFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  /** Visible label. Pass `null` / omit to render an `sr-only` label instead. */
  label?: string;
  srOnlyLabel?: string;
  description?: React.ReactNode;
  required?: boolean;
  /** Number of digits. Defaults to `6`. */
  length?: number;
  className?: string;
  /** Auto-submit the parent form when the last digit is entered. */
  onComplete?: (value: string) => void;
  /** Render the slots centered. Defaults to `true`. */
  centered?: boolean;
}

export const FormOtpField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  srOnlyLabel = 'One-Time Password',
  description,
  required,
  length = 6,
  className,
  onComplete,
  centered = true,
}: FormOtpFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={cn(centered && 'flex flex-col items-center', className)}>
        {label ? (
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
        ) : (
          <FormLabel className="sr-only">{srOnlyLabel}</FormLabel>
        )}
        <FormControl>
          <InputOTP
            maxLength={length}
            value={(field.value as string) || ''}
            onChange={(value) => {
              field.onChange(value);
              if (value.length === length) onComplete?.(value);
            }}
            onBlur={field.onBlur}
            ref={field.ref}
          >
            <InputOTPGroup>
              {Array.from({ length }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormOtpField.displayName = 'FormOtpField';
